// アプリ内ホイールズーム。
//
// ブラウザ標準の Ctrl+ / Cmd+ 拡大はオーバーレイ等の UI が一緒に拡大されて
// 乱れるため、視聴コンテンツのみをスケールする手段を別途用意する。
//
// 棲み分け:
// - トラックパッドのピンチ (ctrlKey 付き wheel) はブラウザのデフォルト拡大に委ねる
// - マウスホイール (ctrlKey なし) はアプリ内ズーム
//
// 実装方針:
// - #video-grid に transform: translate(tx, ty) scale(s), origin 0 0 を当てる
// - 倍率はカーソル位置を不動点としたカーソル中心ズーム
// - 倍率 > 1 のとき左ドラッグでパン、ダブルクリックでリセット
// - 3D room レイアウトでは無効 (視点回転と衝突するため)
// - 自由配置では .free-item / .free-resize 上の pointerdown は既存ハンドラに譲り、
//   空白部分のみパン対象とする
// - ブラウザ拡大 (devicePixelRatio 変化) で起こる scroll パンとは別系統。
//   この機能の transform はレイアウトサイズに影響しないので両者は干渉しない

import { clamp } from "./utils.js";

interface StageZoomDeps {
  stage: HTMLElement;
  grid: HTMLElement;
  isLayoutZoomable: () => boolean;
}

const MIN_SCALE = 1.0;
const MAX_SCALE = 4.0;
// wheel deltaY ピクセル当たりの拡縮係数 (exp ベース)。
// 1 ノッチ ~100px 想定で約 1.16x、3 ノッチで約 1.57x。
const WHEEL_FACTOR = 0.0015;

const state = { scale: 1, tx: 0, ty: 0 };
let panning = false;
let panStart = { x: 0, y: 0, tx: 0, ty: 0 };
let activePointerId: number | null = null;
let panMoved = false;
let deps: StageZoomDeps | null = null;

function isActive(): boolean {
  return state.scale > MIN_SCALE + 0.0001 || state.tx !== 0 || state.ty !== 0;
}

function applyTransform(): void {
  if (!deps) return;
  if (!isActive()) {
    deps.grid.style.removeProperty("transform");
    deps.grid.style.removeProperty("transform-origin");
    delete deps.stage.dataset.appZoom;
    delete deps.stage.dataset.appZoomPanning;
    return;
  }
  deps.grid.style.transformOrigin = "0 0";
  deps.grid.style.transform = `translate(${state.tx}px, ${state.ty}px) scale(${state.scale})`;
  deps.stage.dataset.appZoom = "true";
}

export function resetStageZoom(): void {
  state.scale = MIN_SCALE;
  state.tx = 0;
  state.ty = 0;
  panning = false;
  activePointerId = null;
  panMoved = false;
  applyTransform();
}

function onWheel(e: WheelEvent): void {
  // ピンチはブラウザの標準拡大に任せる (ctrlKey 付き wheel = ピンチ)
  if (e.ctrlKey) return;
  if (!deps || !deps.isLayoutZoomable()) return;
  e.preventDefault();
  const oldScale = state.scale;
  const factor = Math.exp(-e.deltaY * WHEEL_FACTOR);
  const newScale = clamp(oldScale * factor, MIN_SCALE, MAX_SCALE);
  if (newScale === oldScale) return;

  // カーソル中心ズーム: cursor 下のグリッドローカル座標を不変に保つ
  const rect = deps.grid.getBoundingClientRect();
  const gx = (e.clientX - rect.left) / oldScale;
  const gy = (e.clientY - rect.top) / oldScale;
  state.tx = state.tx - gx * (newScale - oldScale);
  state.ty = state.ty - gy * (newScale - oldScale);
  state.scale = newScale;

  // 1.0 に戻ったら translate もゼロに戻して inline style をクリア
  if (state.scale <= MIN_SCALE + 0.0001) {
    state.scale = MIN_SCALE;
    state.tx = 0;
    state.ty = 0;
  }
  applyTransform();
}

function onPointerDown(e: PointerEvent): void {
  if (!deps) return;
  if (!deps.isLayoutZoomable()) return;
  if (state.scale <= MIN_SCALE + 0.0001) return;
  if (e.button !== 0) return;
  const target = e.target as HTMLElement | null;
  if (target?.closest(".stage-overlay")) return;
  // 自由配置: アイテム本体 / リサイズハンドル上は既存ドラッグに譲る
  if (target?.closest(".free-item")) return;

  panning = true;
  panMoved = false;
  activePointerId = e.pointerId;
  panStart = { x: e.clientX, y: e.clientY, tx: state.tx, ty: state.ty };
  deps.stage.dataset.appZoomPanning = "true";
  try {
    deps.stage.setPointerCapture(e.pointerId);
  } catch {
    // 一部 target ではキャプチャに失敗するが致命的ではない
  }
  e.preventDefault();
}

function onPointerMove(e: PointerEvent): void {
  if (!panning || e.pointerId !== activePointerId) return;
  const dx = e.clientX - panStart.x;
  const dy = e.clientY - panStart.y;
  if (!panMoved && Math.hypot(dx, dy) > 3) panMoved = true;
  state.tx = panStart.tx + dx;
  state.ty = panStart.ty + dy;
  applyTransform();
}

function onPointerUp(e: PointerEvent): void {
  if (!deps) return;
  if (e.pointerId !== activePointerId) return;
  panning = false;
  activePointerId = null;
  delete deps.stage.dataset.appZoomPanning;
  try {
    deps.stage.releasePointerCapture(e.pointerId);
  } catch {
    // already released
  }
}

function onClickCapture(e: MouseEvent): void {
  // ドラッグパン直後に発火する click を抑止 (動画クリックの swap 等を誤発火させない)
  if (panMoved) {
    e.stopPropagation();
    e.preventDefault();
    panMoved = false;
  }
}

function onDoubleClick(e: MouseEvent): void {
  if (!deps || !deps.isLayoutZoomable()) return;
  if (!isActive()) return;
  e.preventDefault();
  e.stopPropagation();
  resetStageZoom();
}

export function initStageZoom(d: StageZoomDeps): void {
  deps = d;
  // passive:false で preventDefault 可能にする (ページスクロール抑止)
  deps.stage.addEventListener("wheel", onWheel, { passive: false });
  deps.stage.addEventListener("pointerdown", onPointerDown);
  deps.stage.addEventListener("pointermove", onPointerMove);
  deps.stage.addEventListener("pointerup", onPointerUp);
  deps.stage.addEventListener("pointercancel", onPointerUp);
  // capture で click を見て、ドラッグ直後の click を握り潰す
  deps.stage.addEventListener("click", onClickCapture, { capture: true });
  deps.stage.addEventListener("dblclick", onDoubleClick);
}
